/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, StandardLoadOptions, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { HouseholdService, IHouseholdSearchFilters, countHouseholdFilters } from "app/cd/services/household-service";
import { IPopup } from 'app/shared/elements/popup-base';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/api/api';
import { validate } from 'app/shared/elements/ess-rule';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { microTask } from 'app/shared/lib/decorators';
import { guid } from 'app/shared/lib/guid';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class HouseholdSelect extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected household: CD.Household = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.twoWay })
    protected createHouseholdOpen: boolean = false;

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    private searchFilters: IHouseholdSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    private tempSearchFilters: IHouseholdSearchFilters = {};

    @bindable.boolean
    protected autoSelectOnlyResult: boolean = true;

    @bindable.boolean
    protected searchOnly: boolean = false;

    private householdResults: ReadonlyArray<CD.Household> | null = null;

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize = 25;

    protected validateForm = validate;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $table!: EssTableCustomElement<CD.Household[]>;
    protected $currentCriteriaHeader!: HTMLElement;

    protected id: guid = guid();
    protected id2: guid = guid();

    protected useSpecificFilters: boolean = false;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: HouseholdService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.autoSelectOnlyResult = false; //disable open dialog on sort when one record
        // only trigger search if there is data to sort
        if(this.householdResults?.length) await this.search();
    }


    ////****************** Functions ************************************************////
    public async generalSearch(searchTerm: string): Promise<void> {
        this.searchFilters.generalSearchTerm = searchTerm;
        await this.search(true);
    }

    public async search(openModal: boolean = false): Promise<void> {
        this.useSpecificFilters = this.$modal.state == OpenFlags.Open;
        await this.searchHouseholds();

        if(this.householdResults?.length === 1 && this.resultCount === 1 && this.autoSelectOnlyResult) {
            this.selectHousehold(this.householdResults[0]);
        }
        else if(openModal) {
            this.$modal.open();
        }
        this.autoSelectOnlyResult = true;
        this.openAllDetails();
    }

    protected async handleSearchButton(): Promise<void> {
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        this.searchFilters = { ...this.tempSearchFilters };
        await this.search();
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    public selectHousehold(household: CD.Household) {
        this.searchFilters = {};
        this.household = household;
        this.clear();
        this.$modal.close();
    }

    protected closeModal(): void {
        this.clear();
        this.$modal.close();
    }

    public clear(returnFocus?: boolean): void {
        this.householdResults = new Array<CD.Household>();
        this.searchFilters = {};
        this.tempSearchFilters = {};
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        this.$collapsible.open(0);
        this.useSpecificFilters = false;
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }

    protected householdCleared(): void {
        this.household = {};
        this.searchFilters.generalSearchTerm = null;
        this.createHouseholdOpen = false;
    }

    protected editSelectedHousehold(): void {
        this.tempSearchFilters = this.searchFilters = {};
        this.createHouseholdOpen = false;
        this.householdResults = new Array<CD.Household>();
        this.resultCount = 0;
        this.$collapsible.open(0);
        this.$modal.open();
    }

    protected openCreateHousehold(): void {
        this.searchFilters.generalSearchTerm = null;
        this.createHouseholdOpen = true;
        this.household = {};
    }

    // give data time to bind to the table before expanding details
    @microTask
    protected openAllDetails() {
        this.$table.openAll();
    }


    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        return true;
    }
    public reset?(): void {
        this.clear();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async searchHouseholds(): Promise<void> {
        if(countHouseholdFilters(this.searchFilters) == 0) {
            this.$collapsible.open(0);
            if(this.$modal.state == OpenFlags.Open) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.minimumFilterRequirements')).show();
            }
            this.householdResults = [];
            return;
        } else {
            this.$collapsible.open(1);
        }

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes = [
            'memberships',
            'memberships.member',
            'memberships.member.primaryPlan',
            'memberships.member.primaryPlan.healthPlan',
            'memberships.member.primaryPlan.lineOfBusiness',
            'memberships.member.primaryCarePhysician'
        ]

        try {
            this.state.beginLoad();
            const response = await this.service.search(this.searchFilters, options, includes);
            this.householdResults = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
